import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../utils/api";
import { FAQ, FAQResponse } from "../../types/cs";

interface UseFAQParams {
  pageSize?: number;
}

interface UseFAQReturn {
  faqs: FAQ[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

const useFAQ = (params: UseFAQParams = {}): UseFAQReturn => {
  const { pageSize = 20 } = params;
  
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchFAQs = useCallback(async (page: number = 1, isAppend: boolean = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError("");

    try {
      const response = await apiClient.get("/api/v1/cs/faq", { 
        params: { p: page }
      });
      
      const responseData: FAQResponse = response.data;

      if (responseData.status) {
        const newFAQs = responseData.data;
        
        if (isAppend) {
          setFaqs(prev => [...prev, ...newFAQs]);
        } else {
          setFaqs(newFAQs);
        }

        setHasMore(newFAQs.length >= pageSize);
        setCurrentPage(page);
      } else {
        setError("FAQ를 불러오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("FAQ 조회 오류:", err);
      setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [pageSize]);

  const loadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore && !isLoading) {
      await fetchFAQs(currentPage + 1, true);
    }
  }, [hasMore, isLoadingMore, isLoading, currentPage, fetchFAQs]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchFAQs(1, false);
  }, [fetchFAQs]);

  useEffect(() => {
    fetchFAQs(1, false);
  }, [fetchFAQs]);

  return {
    faqs,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  };
};

export default useFAQ;
export type { UseFAQParams, UseFAQReturn }; 